import { DownOutlined, PlusOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  App,
  Button,
  Dropdown,
  Empty,
  Input,
  Modal,
  Radio,
  Segmented,
  Space,
  Spin,
  Table,
  Tabs,
  Tag,
  Typography,
} from 'antd';
import type { MenuProps } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { getApiError } from '../../api/client';
import type { SubscriptionListItem, SubscriptionGeneratedInvoiceRef } from '../../api/types';
import { DateText, MoneyText } from '../../lib/format';
import { useIsMobile } from '../../lib/responsive';
import { usePersistedState } from '../../lib/usePersistedState';
import { useFirmaCurenta } from '../firme/FirmaCurentaContext';
import { AbonamentFormDrawer } from './AbonamentFormDrawer';
import { deleteAbonament, fetchAbonament, fetchAbonamente, generateNowAbonament, pauseAbonament, resumeAbonament } from './api';
import { GenerationLogTab } from './GenerationLogTab';

const { Title, Text } = Typography;
const PAGE_SIZE = 20;

type StatusFilter = 'ALL' | 'ACTIVE' | 'PAUSED' | 'ENDED';

const SEGMENT_OPTIONS: { label: string; value: StatusFilter }[] = [
  { label: 'Toate', value: 'ALL' },
  { label: 'Active', value: 'ACTIVE' },
  { label: 'Pauzate', value: 'PAUSED' },
  { label: 'Încheiate', value: 'ENDED' },
];

const FREQUENCY_LABELS: Record<string, string> = {
  MONTHLY: 'Lunar',
  QUARTERLY: 'Trimestrial',
  ANNUAL: 'Anual',
};

function StatusTag({ status }: { status: SubscriptionListItem['status'] }) {
  if (status === 'ACTIVE') return <Tag color="green">Activ</Tag>;
  if (status === 'PAUSED') return <Tag color="default">Pauzat</Tag>;
  return <Tag color="default">Încheiat</Tag>;
}

/** The invoice list a subscription's expanded row shows — the last 10 it
 * generated, per `SubscriptionsService.findOne()`. Fetched lazily: this
 * component only mounts once its row is actually expanded. */
function GeneratedInvoicesPanel({ subscriptionId }: { subscriptionId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['subscriptions', 'detail', subscriptionId],
    queryFn: () => fetchAbonament(subscriptionId),
  });

  if (isLoading) return <Spin size="small" />;
  const invoices = data?.generatedInvoices ?? [];
  if (invoices.length === 0) return <Text type="secondary">Nicio factură generată încă.</Text>;

  const columns: ColumnsType<SubscriptionGeneratedInvoiceRef> = [
    {
      title: 'Factură',
      key: 'number',
      render: (_, invoice) => (
        <Link to={`/facturi/${invoice.id}`}>{invoice.fullNumber ?? <Tag>Ciornă</Tag>}</Link>
      ),
    },
    { title: 'Perioadă', key: 'period', render: (_, invoice) => <DateText value={invoice.periodStart} /> },
    {
      title: 'Status',
      key: 'status',
      render: (_, invoice) => {
        if (invoice.status === 'DRAFT') return <Tag>Ciornă</Tag>;
        if (invoice.status === 'CANCELLED') return <Tag color="default">Anulată</Tag>;
        return <Tag color="blue">Emisă</Tag>;
      },
    },
    { title: 'Total', key: 'total', align: 'right', render: (_, invoice) => <MoneyText value={invoice.totalAmount} /> },
  ];

  return <Table<SubscriptionGeneratedInvoiceRef> rowKey="id" size="small" pagination={false} dataSource={invoices} columns={columns} />;
}

/**
 * `/abonamente` (task-8d-brief.md §2): the subscriptions list (with pause /
 * resume / generate-now / delete lifecycle actions and an expandable
 * "last generated invoices" row) plus its secondary "Istoric generări" tab.
 */
export function AbonamenteListPage() {
  const { message, modal } = App.useApp();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { firmaId } = useFirmaCurenta();
  const isMobile = useIsMobile();

  const [activeTab, setActiveTab] = usePersistedState('abonamente.tab', 'abonamente');
  const [filter, setFilter] = usePersistedState<StatusFilter>('abonamente.filter', 'ALL');
  const [page, setPage] = usePersistedState('abonamente.page', 1);
  const [search, setSearch] = usePersistedState('abonamente.search', '');

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [prefillInvoiceId, setPrefillInvoiceId] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Deep links that open the drawer, then drop their query param so a refresh
  // or back-navigation doesn't reopen it:
  //  - `?abonament=<id>`  -> edit that subscription (from an invoice's
  //    "Generată din abonament" section).
  //  - `?dinFactura=<id>` -> a NEW subscription pre-filled from that invoice.
  useEffect(() => {
    const editId = searchParams.get('abonament');
    if (editId) {
      setEditingId(editId);
      setPrefillInvoiceId(null);
      setDrawerOpen(true);
      setSearchParams({}, { replace: true });
      return;
    }
    const fromInvoice = searchParams.get('dinFactura');
    if (fromInvoice) {
      setEditingId(null);
      setPrefillInvoiceId(fromInvoice);
      setDrawerOpen(true);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const [resumeTargetId, setResumeTargetId] = useState<string | null>(null);
  const [resumeCatchUp, setResumeCatchUp] = useState(false);

  const listParams = {
    page,
    pageSize: PAGE_SIZE,
    search: search || undefined,
    status: filter === 'ALL' ? undefined : filter,
  };

  const { data, isLoading } = useQuery({
    queryKey: ['subscriptions', firmaId, listParams],
    queryFn: () => fetchAbonamente({ companyId: firmaId as string, ...listParams }),
    enabled: !!firmaId && activeTab === 'abonamente',
  });

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
  }

  function reportError(error: unknown, fallback: string) {
    message.error(getApiError(error)?.message ?? fallback);
  }

  const pauseMutation = useMutation({
    mutationFn: pauseAbonament,
    onSuccess: () => {
      message.success('Abonamentul a fost pus în pauză.');
      invalidate();
    },
    onError: (error) => reportError(error, 'Abonamentul nu a putut fi pus în pauză.'),
  });

  const resumeMutation = useMutation({
    mutationFn: ({ id, catchUp }: { id: string; catchUp: boolean }) => resumeAbonament(id, { catchUp }),
    onSuccess: () => {
      message.success('Abonamentul a fost reluat.');
      invalidate();
      setResumeTargetId(null);
    },
    onError: (error) => reportError(error, 'Abonamentul nu a putut fi reluat.'),
  });

  const deleteMutation = useMutation({
    mutationFn: deleteAbonament,
    onSuccess: () => {
      message.success('Abonamentul a fost șters.');
      invalidate();
    },
    onError: (error) => reportError(error, 'Abonamentul nu a putut fi șters.'),
  });

  const generateNowMutation = useMutation({
    mutationFn: generateNowAbonament,
    onSuccess: (result) => {
      invalidate();
      modal.success({
        title: 'Factură generată',
        content: result.fullNumber
          ? `Factura ${result.fullNumber} a fost emisă din abonament.`
          : 'Factura a fost creată ca ciornă din abonament.',
        okText: 'Vezi factura',
        cancelText: 'Închide',
        okCancel: true,
        onOk: () => navigate(`/facturi/${result.id}`),
      });
    },
    onError: (error) => reportError(error, 'Factura nu a putut fi generată.'),
  });

  function menuFor(row: SubscriptionListItem): MenuProps['items'] {
    const isActive = row.status === 'ACTIVE';
    const isPaused = row.status === 'PAUSED';
    const canDelete = row.generatedCount === 0;
    return [
      { key: 'edit', label: 'Editează' },
      ...(isActive ? [{ key: 'pause', label: 'Pauză' }] : []),
      ...(isPaused ? [{ key: 'resume', label: 'Reia' }] : []),
      ...(isActive ? [{ key: 'generate-now', label: 'Generează acum' }] : []),
      ...(canDelete ? [{ type: 'divider' as const }, { key: 'delete', label: 'Șterge', danger: true }] : []),
    ];
  }

  function handleAction(row: SubscriptionListItem, key: string) {
    switch (key) {
      case 'edit':
        setEditingId(row.id);
        setDrawerOpen(true);
        return;
      case 'pause':
        pauseMutation.mutate(row.id);
        return;
      case 'resume':
        setResumeCatchUp(false);
        setResumeTargetId(row.id);
        return;
      case 'generate-now':
        modal.confirm({
          title: 'Generezi factura acum?',
          content: `Se va genera o factură pentru perioada curentă a abonamentului (începând cu ${dayjs(row.nextIssueDate).format('DD.MM.YYYY')}) și cursorul abonamentului avansează la următoarea perioadă. Continui?`,
          okText: 'Generează',
          cancelText: 'Renunță',
          onOk: () => generateNowMutation.mutateAsync(row.id),
        });
        return;
      case 'delete':
        modal.confirm({
          title: 'Ștergi abonamentul?',
          content: 'Abonamentul se șterge definitiv. Această acțiune este permisă doar cât timp nu a generat nicio factură.',
          okText: 'Șterge',
          cancelText: 'Renunță',
          okButtonProps: { danger: true },
          onOk: () => deleteMutation.mutateAsync(row.id),
        });
        return;
      default:
        return;
    }
  }

  const columns: ColumnsType<SubscriptionListItem> = [
    {
      title: 'Denumire abonament',
      key: 'name',
      render: (_, row) => (
        <a
          onClick={(event) => {
            event.preventDefault();
            handleAction(row, 'edit');
          }}
        >
          {row.name}
        </a>
      ),
    },
    { title: 'Client', dataIndex: 'clientName' },
    {
      title: 'Valoare/perioadă',
      key: 'monthlyValue',
      align: 'right',
      render: (_, row) => <MoneyText value={row.monthlyValue} />,
    },
    {
      title: 'Frecvență',
      key: 'frequency',
      render: (_, row) => <Tag>{FREQUENCY_LABELS[row.frequency] ?? row.frequency}</Tag>,
    },
    {
      title: 'Următoarea emitere',
      key: 'nextIssueDate',
      render: (_, row) => {
        const daysUntil = dayjs(row.nextIssueDate).startOf('day').diff(dayjs().startOf('day'), 'day');
        return (
          <Space size={4}>
            <DateText value={row.nextIssueDate} />
            {daysUntil <= 3 ? <Tag color="red">{daysUntil < 0 ? 'Restant' : 'Curând'}</Tag> : null}
          </Space>
        );
      },
    },
    {
      title: 'Emitere',
      key: 'autoIssue',
      render: (_, row) => (row.autoIssue ? <Tag color="blue">Automată</Tag> : <Tag>Ciornă</Tag>),
    },
    { title: 'Status', key: 'status', render: (_, row) => <StatusTag status={row.status} /> },
    { title: 'Generate', key: 'generatedCount', width: 90, align: 'center', render: (_, row) => row.generatedCount },
    {
      title: 'Acțiuni',
      key: 'actions',
      width: 110,
      render: (_, row) => (
        <span onClick={(event) => event.stopPropagation()} role="presentation">
          <Dropdown menu={{ items: menuFor(row), onClick: ({ key }) => handleAction(row, key) }} trigger={['click']}>
            <Button size="small">
              Acțiuni <DownOutlined />
            </Button>
          </Dropdown>
        </span>
      ),
    },
  ];

  return (
    <div>
      <div
        style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'stretch' : 'center',
          justifyContent: 'space-between',
          gap: 8,
          marginBottom: 16,
        }}
      >
        <Title level={3} style={{ margin: 0 }}>
          Abonamente
        </Title>
        {activeTab === 'abonamente' ? (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            block={isMobile}
            disabled={!firmaId}
            onClick={() => {
              setEditingId(null);
              setDrawerOpen(true);
            }}
          >
            Abonament nou
          </Button>
        ) : null}
      </div>

      {!firmaId ? (
        <Empty description="Selectează sau adaugă mai întâi o firmă." />
      ) : (
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: 'abonamente',
              label: 'Abonamente',
              children: (
                <>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: isMobile ? 'column' : 'row',
                      flexWrap: 'wrap',
                      alignItems: isMobile ? 'stretch' : 'center',
                      gap: 12,
                      marginBottom: 16,
                    }}
                  >
                    <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
                      <Segmented<StatusFilter>
                        options={SEGMENT_OPTIONS}
                        value={filter}
                        onChange={(value) => {
                          setFilter(value);
                          setPage(1);
                        }}
                      />
                    </div>
                    <Input.Search
                      placeholder="Caută după denumire sau client"
                      allowClear
                      defaultValue={search}
                      style={{ width: isMobile ? '100%' : 280 }}
                      onSearch={(value) => {
                        setSearch(value);
                        setPage(1);
                      }}
                    />
                  </div>

                  <Table<SubscriptionListItem>
                    rowKey="id"
                    columns={columns}
                    dataSource={data?.items ?? []}
                    loading={isLoading}
                    scroll={{ x: 'max-content' }}
                    expandable={{
                      expandedRowRender: (row) => <GeneratedInvoicesPanel subscriptionId={row.id} />,
                      rowExpandable: () => true,
                    }}
                    pagination={{
                      current: page,
                      pageSize: PAGE_SIZE,
                      total: data?.total ?? 0,
                      onChange: setPage,
                      showSizeChanger: false,
                    }}
                    summary={() =>
                      data && data.items.length > 0 ? (
                        <Table.Summary fixed>
                          <Table.Summary.Row>
                            {/* Leading empty cell aligns under the expander column. */}
                            <Table.Summary.Cell index={0} />
                            <Table.Summary.Cell index={1} colSpan={2}>
                              <Text strong>Total (filtrat, toate paginile)</Text>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={3} align="right">
                              <Text strong>
                                <MoneyText value={data.totals.monthlyValue} />
                              </Text>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={4} colSpan={6} />
                          </Table.Summary.Row>
                        </Table.Summary>
                      ) : null
                    }
                  />
                </>
              ),
            },
            {
              key: 'istoric',
              label: 'Istoric generări',
              children: <GenerationLogTab firmaId={firmaId} />,
            },
          ]}
        />
      )}

      {firmaId ? (
        <AbonamentFormDrawer
          open={drawerOpen}
          firmaId={firmaId}
          editingId={editingId}
          prefillInvoiceId={prefillInvoiceId}
          onClose={() => {
            setDrawerOpen(false);
            setPrefillInvoiceId(null);
          }}
        />
      ) : null}

      <Modal
        open={resumeTargetId !== null}
        title="Reiei abonamentul?"
        okText="Reia"
        cancelText="Renunță"
        confirmLoading={resumeMutation.isPending}
        onCancel={() => setResumeTargetId(null)}
        onOk={() => resumeTargetId && resumeMutation.mutate({ id: resumeTargetId, catchUp: resumeCatchUp })}
      >
        <Text style={{ display: 'block', marginBottom: 12 }}>
          Ce se întâmplă cu perioadele ratate în timp ce abonamentul a fost în pauză?
        </Text>
        <Radio.Group value={resumeCatchUp} onChange={(event) => setResumeCatchUp(event.target.value)}>
          <Space direction="vertical">
            <Radio value={false}>Sari peste perioadele ratate — cursorul avansează direct la următoarea perioadă viitoare, fără facturi pentru cele trecute.</Radio>
            <Radio value={true}>Facturează perioadele ratate — fiecare perioadă ratată primește câte o factură, generate pe rând.</Radio>
          </Space>
        </Radio.Group>
      </Modal>
    </div>
  );
}
